import type { KeyboardEvent } from "react";
import Field from "../../../ui/forms/Field";
import { classNames } from "../../../ui/styles/classNames";
import { FORM_CONTROL_CLASS } from "../../../ui/styles/formStyles";
import SearchSyntaxHelp, { type FuzzySupport, type KeyHelp } from "./SearchSyntaxHelp";

/** A field that drives a result list as a combobox (WAI-ARIA APG): it keeps the focus and names the active option. */
export interface ComboboxWiring {
  readonly listId: string;
  /** `undefined` while the list has no options. */
  readonly activeOptionId: string | undefined;
  readonly onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => void;
}

interface SearchQueryFieldProps {
  query: string;
  onQueryChange: (query: string) => void;
  fuzzy: FuzzySupport;
  /** What the field searches, read by screen readers and shown as the placeholder. */
  label: string;
  combobox?: ComboboxWiring | undefined;
  /** What Enter does, such as opening the first result (F135); without it, nothing. */
  onEnter?: (() => void) | undefined;
  /** What the syntax help says of documents' keys; nothing by default. */
  keyHelp?: KeyHelp;
}

/** A query in DMW's search syntax, with the syntax help beneath it. */
export default function SearchQueryField({ query, onQueryChange, fuzzy, label, combobox, onEnter, keyHelp = "none" }: SearchQueryFieldProps) {
  return (
    <div className="space-y-1">
      <Field labelHidden label={label}>
        {(control) => (
          <input
            {...control}
            type="search"
            value={query}
            placeholder={label}
            className={classNames(FORM_CONTROL_CLASS, "text-sm")}
            onChange={(event) => { onQueryChange(event.target.value); }}
            onKeyDown={(event) => {
              combobox?.onKeyDown(event);
              if (event.defaultPrevented || event.key !== "Enter" || onEnter === undefined) return;
              event.preventDefault();
              onEnter();
            }}
            {...(combobox === undefined ? {} : {
              role: "combobox",
              autoFocus: true,
              "aria-autocomplete": "list" as const,
              "aria-expanded": true,
              "aria-controls": combobox.listId,
              "aria-activedescendant": combobox.activeOptionId,
            })}
          />
        )}
      </Field>
      <SearchSyntaxHelp fuzzy={fuzzy} keyHelp={keyHelp} />
    </div>
  );
}
